import { parseUnit } from './index'
import { MIN_WIDTH, PREFIX, SPECIAL_CULUMN_TYPES } from './config'

export default class ColumnManager {
  constructor (columns) {
    this.columns = columns
    this._cached = {}
  }
  // 有没有固定列
  isAnyColumnsFixed () {
    return this._cache('isAnyColumnsFixed', () =>
      this.columns.some(column => !!column.fixed)
    )
  }
  // 有没有左侧固定列
  isAnyColumnsLeftFixed () {
    return this._cache('isAnyColumnsLeftFixed', () =>
      this.columns.some(
        column => column.fixed === 'left' || column.fixed === true
      )
    )
  }
  // 有没有右侧固定列
  isAnyColumnsRightFixed () {
    return this._cache('isAnyColumnsRightFixed', () =>
      this.columns.some(column => column.fixed === 'right')
    )
  }
  // 左侧固定列
  leftColumns () {
    return this._cache('leftColumns', () =>
      this.groupedColumns().filter(
        column => column.fixed === 'left' || column.fixed === true
      )
    )
  }
  // 右侧固定列
  rightColumns () {
    return this._cache('rightColumns', () =>
      this.groupedColumns().filter(column => column.fixed === 'right')
    )
  }

  // 左侧固定列宽度
  leftColumnsWidth () {
    return this._cache('leftColumnsWidth', () =>
      this.leftColumns().reduce((pre, next) => pre + next.hisWidth, 0)
    )
  }

  // 右侧固定列宽度
  rightColumnsWidth () {
    return this._cache('rightColumnsWidth', () =>
      this.rightColumns().reduce((pre, next) => pre + next.hisWidth, 0)
    )
  }

  // 总宽度
  columnsWidth () {
    return this._cache('columnsWidth', () =>
      this.columns.reduce((pre, next) => pre + next.hisWidth, 0)
    )
  }

  // 是否全部为fixed
  isAllFixed () {
    return this.columns.every(column => column.fixed)
  }

  // add appropriate rowspan and colspan to column
  groupedColumns () {
    return this._cache('groupedColumns', () => {
      const _groupColumns = (
        columns,
        currentRow = 0,
        parentColumn = {},
        rows = []
      ) => {
        // track how many rows we got
        rows[currentRow] = rows[currentRow] || []
        const grouped = []
        const setRowSpan = column => {
          const rowSpan = rows.length - currentRow
          if (
            column &&
            !column.children && // parent columns are supposed to be one row
            rowSpan > 1 &&
            (!column.rowSpan || column.rowSpan < rowSpan)
          ) {
            column.rowSpan = rowSpan
          }
        }
        columns.forEach((column, index) => {
          const newColumn = { ...column }
          rows[currentRow].push(newColumn)
          parentColumn.colSpan = parentColumn.colSpan || 0
          if (newColumn.children && newColumn.children.length > 0) {
            newColumn.children = _groupColumns(
              newColumn.children,
              currentRow + 1,
              newColumn,
              rows
            )
            parentColumn.colSpan += newColumn.colSpan
          } else {
            parentColumn.colSpan += 1
          }
          // update rowspan to all same row columns
          for (let i = 0; i < rows[currentRow].length - 1; i += 1) {
            setRowSpan(rows[currentRow][i])
          }
          // last column, update rowspan immediately
          if (index + 1 === columns.length) {
            setRowSpan(newColumn)
          }
          grouped.push(newColumn)
        })
        return grouped
      }
      return _groupColumns(this.columns)
    })
  }

  resetColumns (columns) {
    this.columns = columns
    this._cached = {}
  }

  _cache (name, fn) {
    if (name in this._cached) {
      return this._cached[name]
    }
    this._cached[name] = fn()
    return this._cached[name]
  }

  _leafColumns (columns) {
    const leafColumns = []
    columns.forEach(column => {
      if (!column.children) {
        leafColumns.push(column)
      } else {
        leafColumns.push(...this._leafColumns(column.children))
      }
    })
    return leafColumns
  }

  // 需要更新的columns
  flattenColumns () {
    return this._cache('flattenColumns', () => {
      return this.groupedColumns().filter(this.isNeedUpdate)
    })
  }

  // 需要更新的columns
  flattenWidth () {
    return this._cache('flattenWidth', () =>
      this.flattenColumns().reduce((pre, next) => pre + next.hisWidth, 0)
    )
  }

  // 不需要设置的宽度
  fixedWidth () {
    return this._cache(
      'fixedWidth',
      () => this.columnsWidth() - this.flattenWidth()
    )
  }

  // 是否需要更新
  isNeedUpdate (column) {
    return !column.fixed && !SPECIAL_CULUMN_TYPES.includes(column.type) && column.width === 'auto'
  }

  // 重置宽度
  resetColumnsWidth (isScroll) {
    if (isScroll) {
      this.columns.forEach(column => {
        column.realWidth = column.hisWidth
      })
    } else {
      let isUpdate = false
      let updateCols = []
      this.columns.forEach(column => {
        if (!column.fixed && !SPECIAL_CULUMN_TYPES.includes(column.type)) {
          if (column.width === 'auto') {
            column.realWidth = 'auto'
            isUpdate = true
          } else {
            updateCols.push(column)
          }
        }
      })
      if (!isUpdate) {
        updateCols.forEach(col => {
          col.realWidth = 'auto'
        })
      }
    }
  }
}

// 处理 column宽度
export function processWidth (column) {
  if (SPECIAL_CULUMN_TYPES.includes(column.type)) { return }
  if (!column.width) {
    column.width = 'auto'
  }
  if (!column.minWidth) {
    column.minWidth = 'auto'
  }
  if (column.fixed) {
    if (column.width === 'auto') {
      column.width = MIN_WIDTH
      column.hisWidth = MIN_WIDTH
      column.realWidth = MIN_WIDTH
      return
    }
  }
  const { minWidth, width } = column
  if (minWidth === 'auto' && width === 'auto') {
    column.realWidth = 'auto'
    column.hisWidth = MIN_WIDTH
  } else {
    let reWidth = parseUnit(minWidth === 'auto' ? width : minWidth)
    column.hisWidth = reWidth
    column.realWidth = reWidth
  }
}
// 处理 column ClassName
export function processClass (column, columns = [], index) {
  let { align, fixed, dataIndex } = column
  let cellClassName = ` ${PREFIX}-column ${PREFIX}-column-align-${align}`
  if (fixed) {
    cellClassName += ` ${PREFIX}-column-fixed-${fixed === 'right' ? 'right' : 'left'}`
   }
  if (dataIndex) { cellClassName += ` ${PREFIX}-column-${dataIndex}` }
  // 阴影
  if (column.fixed === 'right') {
    const _column = columns.find(v => v.fixed === 'right') || {}
    if (_column.dataIndex === dataIndex) {
      cellClassName += ` ${PREFIX}-column-right-shadow`
    }
  } else if (!column.fixed && index !== 0) {
    const proCol = columns[index - 1]
    if (proCol.fixed && proCol.fixed !== 'right') {
      proCol.cellClassName += ` ${PREFIX}-column-left-shadow`
    }
  }
  column.cellClassName = cellClassName
}

// 处理 style
export function processStyle (column, cols, leftPos = 0) {
  if (column.fixed === 'right') {
    let rightPos = 0
    for (let i = cols.length - 1; i >= 0; i--) {
      if (cols[i].dataIndex === column.dataIndex) {
        break
      }
      rightPos += column.hisWidth
    }
    column.style = { right: `${rightPos}px` }
  } else if (column.fixed) {
    column.style = { left: `${leftPos}px` }
    leftPos += column.hisWidth
  } 
  return leftPos
}